import React, { useEffect, useState } from 'react';
import {
  Card, Table, Button, Input, Tag, Avatar, Drawer,
  List, Spin, Statistic, Typography, Space,
} from 'antd';
import {
  UserOutlined, SearchOutlined, DownloadOutlined,
  HistoryOutlined, FileTextOutlined, ShoppingCartOutlined,
  PhoneOutlined, MailOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import moment from 'moment';
import { getUserStatsFromOrders } from '../../services/orderApi';

const { Text, Title } = Typography;

export default function CompanyUsers() {
  const { companyId } = useParams();

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');

  const [historyOpen, setHistoryOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const res = await getUserStatsFromOrders(companyId);
      setUsers(res?.data || []);
    } catch (err) {
      console.error('Error fetching user stats:', err);
    } finally {
      setLoading(false);
    }
  };

  const downloadCSV = () => {
    const headers = ['Name', 'Email', 'Phone', 'Total Visits', 'Total Spent (₹)', 'Last Visit'];
    const rows = users.map(u => [
      u.name || '',
      u.email || '',
      u.phone || '',
      u.totalVisits || 0,
      u.totalSpent || 0,
      u.lastVisit ? moment(u.lastVisit).format('DD/MM/YYYY') : '',
    ]);
    const csv = [headers, ...rows]
      .map(row => row.map(c => `"${String(c).replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `users_${companyId}_${moment().format('YYYYMMDD')}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const filtered = users.filter(u => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.phone || '').includes(q)
    );
  });

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (name) => (
        <Space>
          <Avatar icon={<UserOutlined />} size="small" style={{ backgroundColor: '#1890ff' }} />
          <Text strong>{name}</Text>
        </Space>
      ),
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (v) => <Text type="secondary">{v || '—'}</Text>,
    },
    {
      title: 'Phone',
      dataIndex: 'phone',
      key: 'phone',
      render: (v) => <Text>{v || '—'}</Text>,
    },
    {
      title: 'Total Visits',
      dataIndex: 'totalVisits',
      key: 'totalVisits',
      align: 'center',
      sorter: (a, b) => (a.totalVisits || 0) - (b.totalVisits || 0),
      render: (v) => <Tag color="blue" icon={<HistoryOutlined />}>{v || 0}</Tag>,
    },
    {
      title: 'Total Spent',
      dataIndex: 'totalSpent',
      key: 'totalSpent',
      align: 'center',
      sorter: (a, b) => (a.totalSpent || 0) - (b.totalSpent || 0),
      render: (v) => <Text strong style={{ color: '#52c41a' }}>₹{v || 0}</Text>,
    },
    {
      title: 'Last Visit',
      dataIndex: 'lastVisit',
      key: 'lastVisit',
      sorter: (a, b) => new Date(a.lastVisit) - new Date(b.lastVisit),
      render: (d) => d ? moment(d).format('DD MMM YYYY') : '—',
    },
    {
      title: 'Action',
      key: 'action',
      align: 'center',
      render: (_, record) => (
        <Button
          type="link"
          icon={<FileTextOutlined />}
          onClick={() => { setSelectedUser(record); setHistoryOpen(true); }}
        >
          View History
        </Button>
      ),
    },
  ];

  return (
    <div>
      <Card
        title={
          <Space>
            <UserOutlined style={{ color: '#1890ff' }} />
            <Title level={4} style={{ margin: 0 }}>Users</Title>
          </Space>
        }
        extra={
          <Space wrap>
            <Input
              placeholder="Search name, email or phone"
              prefix={<SearchOutlined />}
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ width: 240 }}
              allowClear
            />
            <Button
              icon={<DownloadOutlined />}
              onClick={downloadCSV}
              disabled={users.length === 0}
            >
              Download CSV
            </Button>
          </Space>
        }
        style={{ borderRadius: 10 }}
      >
        <Table
          dataSource={filtered}
          columns={columns}
          rowKey="_id"
          loading={loading}
          pagination={{ pageSize: 10, showTotal: total => `Total ${total} users` }}
          scroll={{ x: 800 }}
        />
      </Card>

      {/* History Drawer */}
      <Drawer
        title={
          selectedUser && (
            <Space>
              <Avatar icon={<UserOutlined />} style={{ backgroundColor: '#1890ff' }} />
              <div>
                <div style={{ fontWeight: 600 }}>{selectedUser.name}</div>
                <div style={{ fontSize: 12, color: '#888', fontWeight: 400 }}>
                  <PhoneOutlined style={{ marginRight: 4 }} />{selectedUser.phone}
                  {selectedUser.email && (
                    <span style={{ marginLeft: 10 }}>
                      <MailOutlined style={{ marginRight: 4 }} />{selectedUser.email}
                    </span>
                  )}
                </div>
              </div>
            </Space>
          )
        }
        open={historyOpen}
        onClose={() => { setHistoryOpen(false); setSelectedUser(null); }}
        width={560}
        extra={
          selectedUser && (
            <Space size="large">
              <Statistic
                title="Visits"
                value={selectedUser.totalVisits || 0}
                prefix={<HistoryOutlined />}
                valueStyle={{ fontSize: 16 }}
              />
              <Statistic
                title="Total Spent"
                value={`₹${selectedUser.totalSpent || 0}`}
                valueStyle={{ fontSize: 16, color: '#52c41a' }}
              />
            </Space>
          )
        }
      >
        {selectedUser && (
          <List
            dataSource={selectedUser.orders || []}
            locale={{ emptyText: 'No order history found' }}
            renderItem={(order) => (
              <List.Item key={order._id} style={{ padding: '12px 0' }}>
                <div style={{ width: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                    <Text strong style={{ color: '#52c41a' }}>₹{order.totalAmount}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {order.createdAt ? moment(order.createdAt).format('DD MMM YYYY, hh:mm A') : '—'}
                    </Text>
                  </div>
                  <div style={{ marginBottom: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {(order.serviceList || []).map((s, i) => (
                      <Tag key={i} color="blue">{s.name} — ₹{s.price}</Tag>
                    ))}
                  </div>
                  <Space wrap>
                    {order.staff?.userName && (
                      <Tag icon={<UserOutlined />} color="geekblue">{order.staff.userName}</Tag>
                    )}
                    {order.paymentMode && (
                      <Tag icon={<ShoppingCartOutlined />} color={order.paymentMode === 'online' ? 'green' : 'orange'}>
                        {order.paymentMode}
                      </Tag>
                    )}
                  </Space>
                </div>
              </List.Item>
            )}
          />
        )}
      </Drawer>
    </div>
  );
}
